"use client";

import { isFaireDirectCreditEligible } from "@faire/retailer-visitor-shared/lib/signUpRevampUtils";
import { isTabletDevice } from "@faire/retailer-visitor-shared/lib/user-agent/isTabletDevice";
import { useAssignSettingVerticalizedImageryInSignUp } from "@faire/retailer-visitor-shared/settings/useSettingVerticalizedImageryInSignUp";
import { useRetailerSubUser } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/retailerSubUserHooks";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import { useIsSettingVerticalizedImageryInSignUp } from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/__internal__/useIsSettingVerticalizedImageryInSignUp";
import {
  VerticalizedImages,
  HEADER_HEIGHT,
} from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/__internal__/VerticalizedImages";
import { useIsVerticalizedSignUp } from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/useIsVerticalizedSignUp";
import {
  SignUpImage,
  useSignUpImage,
} from "@faire/retailer-visitor-shared/views/SignUp/_verticalized-signup/useSignUpImage";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSignUpStepsStore } from "@faire/retailer-visitor-shared/views/SignUp/SignUpStepsContext";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { Row, Column } from "@faire/web--source/slate/Layout";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import styled, { css } from "styled-components";

export const VerticalizedSignUpLayout: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const {
    signUpImages,
    currentSignUpImage,
    setImagesToReload,
    imagesToReload,
  } = useSignUpImage();
  const isVerticalizedSignUp = useIsVerticalizedSignUp();
  const settingVerticalizedImageryInSignUp =
    useIsSettingVerticalizedImageryInSignUp();
  const assignSettingVerticalizedImageryInSignUp =
    useAssignSettingVerticalizedImageryInSignUp();
  const { isDesktopAndAbove } = useViewport();
  const isTablet = isTabletDevice();
  const [
    { isVerticalizedSignUp: isVerticalizedSignUpFromStore },
    setSignUpRequest,
  ] = useSignUpRequest(["isVerticalizedSignUp"]);

  // This is to avoid a first render with control when it should be treatment
  const isVerticalizedImageryEligible = !isTablet && isDesktopAndAbove;
  const isVerticalizedImageryTreatment =
    isVerticalizedImageryEligible && settingVerticalizedImageryInSignUp;
  const canSeeVerticalized =
    isVerticalizedSignUp ?? isVerticalizedImageryTreatment;

  useEffect(() => {
    if (isVerticalizedSignUpFromStore === undefined) {
      if (!isTablet && isDesktopAndAbove) {
        assignSettingVerticalizedImageryInSignUp();
      }
      setSignUpRequest({
        isVerticalizedSignUp:
          !isTablet && isDesktopAndAbove && settingVerticalizedImageryInSignUp,
      });
    }
  }, [
    isTablet,
    isDesktopAndAbove,
    settingVerticalizedImageryInSignUp,
    isVerticalizedSignUpFromStore,
    setSignUpRequest,
    assignSettingVerticalizedImageryInSignUp,
  ]);

  const location = useLocation();
  const isFD = isFaireDirectCreditEligible(location);
  const [{ currentStep }] = useSignUpStepsStore(["currentStep"]);
  const { isRetailerSubUser } = useRetailerSubUser();
  const loadTimingRef = useSignUpUPLT({
    step: Step.PERSONAL_INFO,
    ignoreImages: false,
    disableUPLT:
      !canSeeVerticalized ||
      currentStep !== Step.PERSONAL_INFO ||
      isRetailerSubUser,
    numComponents: 1,
    options: {
      isFD,
    },
  });

  return (
    <Container $isVerticalizedSignUp={canSeeVerticalized}>
      {canSeeVerticalized ? (
        <ImageColumn>
          <VerticalizedImages
            images={signUpImages}
            currentImageKey={currentSignUpImage}
            resetLoadedImageKeys={imagesToReload}
            onReloadImages={() => setImagesToReload([])}
            loadTimingRef={loadTimingRef}
            timingRefImageKey={SignUpImage.WELCOME}
          />
        </ImageColumn>
      ) : null}
      <ContentColumn $isVerticalizedSignUp={canSeeVerticalized}>
        {children}
      </ContentColumn>
    </Container>
  );
};

const Container = styled(Row).attrs({
  align: "flex-start",
})<{ $isVerticalizedSignUp: boolean }>`
  ${({ $isVerticalizedSignUp }) =>
    $isVerticalizedSignUp &&
    css`
      width: 100%;
      max-width: 1440px;
      margin: auto;
      position: relative;
    `}
`;

const ImageColumn = styled(Column).attrs({
  align: "flex-end",
})`
  width: 50%;
  position: sticky;
  top: ${HEADER_HEIGHT}px;
`;

const ContentColumn = styled(Column)<{ $isVerticalizedSignUp: boolean }>`
  margin: 0 auto;
  ${({ $isVerticalizedSignUp }) =>
    $isVerticalizedSignUp
      ? css`
          width: 560px;
          padding: ${getSpacing("8x", "5x")};
        `
      : css`
          width: 100%;
        `}
`;
